import { type ChangeEvent, type FormEvent } from 'react'

import { Field } from '#app/components/form/field.tsx'
import { SelectField } from '#app/components/form/select-field.tsx'

import { MONITORING_FEED_COPY } from '#app/routes/mocra+/_form+/components/monitoring-feeds/constants.ts'
import {
	type MonitoringFeedDraft,
	type MonitoringFeedFrequencyValue,
	type MonitoringFeedSourceValue,
} from '#app/routes/mocra+/_form+/components/monitoring-feeds/types.ts'
import {
	REGULATORY_FEED_FREQUENCY_OPTIONS,
	REGULATORY_FEED_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'

type DraftErrors = Partial<Record<keyof MonitoringFeedDraft, string>>

type Props = {
	readonly formId: string
	readonly draft: MonitoringFeedDraft
	readonly errors: DraftErrors
	readonly onDraftFieldChange: <Field extends keyof MonitoringFeedDraft>(
		field: Field,
		value: MonitoringFeedDraft[Field],
	) => void
	readonly onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

export function MonitoringFeedForm({
	formId,
	draft,
	errors,
	onDraftFieldChange,
	onSubmit,
}: Props) {
	const handleSourceChange = (event: ChangeEvent<HTMLSelectElement>) => {
		onDraftFieldChange(
			'source',
			event.target.value as MonitoringFeedSourceValue,
		)
	}

	const handleFrequencyChange = (event: ChangeEvent<HTMLSelectElement>) => {
		onDraftFieldChange(
			'frequency',
			event.target.value as MonitoringFeedFrequencyValue,
		)
	}

	const handleDetailChange = (event: ChangeEvent<HTMLInputElement>) => {
		onDraftFieldChange('detail', event.target.value)
	}

	return (
		<form id={formId} onSubmit={onSubmit}>
			<SelectField
				labelProps={{ children: MONITORING_FEED_COPY.fields.sourceLabel }}
				selectProps={{ value: draft.source, onChange: handleSourceChange }}
				options={REGULATORY_FEED_OPTIONS}
			/>
			<SelectField
				labelProps={{ children: MONITORING_FEED_COPY.fields.frequencyLabel }}
				selectProps={{
					value: draft.frequency,
					onChange: handleFrequencyChange,
				}}
				options={REGULATORY_FEED_FREQUENCY_OPTIONS}
			/>
			<Field
				labelProps={{ children: MONITORING_FEED_COPY.fields.detailLabel }}
				inputProps={{
					value: draft.detail,
					onChange: handleDetailChange,
					type: 'text',
				}}
				descriptionProps={{
					children: MONITORING_FEED_COPY.fields.detailDescription,
				}}
				errors={errors.detail ? [errors.detail] : undefined}
			/>
		</form>
	)
}
